/*
  IW Nuevo cart drawer: complementary products with one-tap add (snippets/cart-drawer.liquid,
  sections/iw-cart-recs.liquid).

  - <iw-cart-recs data-url data-product-id> loads the section for the newest cart line: complementary products first
    (Search & Discovery), related products when there are none. Responses are cached per product for the page view,
    because Dawn re-renders the whole drawer on every quantity change and the element is created again each time.
  - "Agregar" posts /cart/add.js asking for the drawer sections and hands the response to the drawer's own
    renderContents, the same path the product form uses, so the drawer, the cart bubble and the live region update
    together. Shopify's Ajax API emits the standard add-to-cart analytics event by itself.
  - Hides floating chat launchers while the drawer is open so they do not cover the drawer's buttons.
*/
(function () {
  if (customElements.get('iw-cart-recs')) return;

  const cache = new Map();

  function fetchRecs(base, productId, intent) {
    const url = `${base}?section_id=iw-cart-recs&product_id=${encodeURIComponent(productId)}&limit=10&intent=${intent}`;
    return fetch(url)
      .then((r) => (r.ok ? r.text() : ''))
      .then((html) => {
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const wrap = doc.querySelector('.iw-cd-recs__wrap');
        return wrap ? wrap.outerHTML : '';
      })
      .catch(() => '');
  }

  function load(base, productId) {
    if (!cache.has(productId)) {
      cache.set(
        productId,
        fetchRecs(base, productId, 'complementary').then((html) => html || fetchRecs(base, productId, 'related'))
      );
    }
    return cache.get(productId);
  }

  class IwCartRecs extends HTMLElement {
    connectedCallback() {
      const productId = this.dataset.productId;
      if (!productId || !this.dataset.url) return;
      load(this.dataset.url, productId).then((html) => {
        if (!this.isConnected || !html) return;
        this.innerHTML = html;
        this.classList.add('is-loaded');
      });
      this.addEventListener('click', this.onClick.bind(this));
    }

    onClick(event) {
      const button = event.target.closest('button.iw-cd-recs__add');
      if (!button || button.disabled) return;
      event.preventDefault();
      const drawer = this.closest('cart-drawer');
      const label = button.querySelector('.iw-cd-recs__add-label');
      button.disabled = true;
      button.setAttribute('aria-busy', 'true');
      if (label) label.textContent = 'Agregando…';

      const addUrl = (window.routes && window.routes.cart_add_url) || '/cart/add';
      fetch(`${addUrl}.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          items: [{ id: Number(button.dataset.variantId), quantity: 1 }],
          sections: 'cart-drawer,cart-icon-bubble',
          sections_url: window.location.pathname,
        }),
      })
        .then((r) => r.json().then((data) => ({ ok: r.ok, data })))
        .then(({ ok, data }) => {
          if (!ok || data.status) throw new Error(data.description || data.message || 'error');
          if (!drawer || typeof drawer.renderContents !== 'function') {
            window.location.href = (window.routes && window.routes.cart_url) || '/cart';
            return;
          }
          drawer.renderContents(data);
          // The drawer is already open, so Dawn's transitionend focus trap does not run again: restore focus here,
          // otherwise it falls to <body> and Escape stops closing the drawer. The target is the footer CTA because
          // the cartUpdate published below makes Dawn re-render cart-drawer-items, which would drop focus again.
          setTimeout(() => {
            if (typeof trapFocus === 'function') {
              trapFocus(drawer, drawer.querySelector('#CartDrawer-Continue') || drawer.querySelector('.drawer__close'));
            }
          });
          // PUB_SUB_EVENTS is a top-level const (constants.js), not a window property.
          if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
            publish(PUB_SUB_EVENTS.cartUpdate, { source: 'iw-cart-recs', productVariantId: button.dataset.variantId, cartData: data });
          }
        })
        .catch(() => {
          button.disabled = false;
          button.removeAttribute('aria-busy');
          if (label) label.textContent = 'No se pudo, reintenta';
        });
    }
  }

  customElements.define('iw-cart-recs', IwCartRecs);

  const onCartPage = () => window.location.pathname === ((window.routes && window.routes.cart_url) || '/cart');

  // On /cart the page already is the cart: a click on the header icon reloads it instead of opening the drawer on
  // top of it (keyboard opening is covered by the open() guard below).
  document.addEventListener(
    'click',
    (event) => {
      if (onCartPage() && event.target.closest('#cart-icon-bubble')) event.stopPropagation();
    },
    true
  );

  // Adds made outside the drawer that only publish cartUpdate (brand landings, other sections) leave the drawer
  // stale: Dawn refreshes only cart-drawer-items, not this drawer's header count and footer. Refresh the whole inner
  // panel without opening it. Sources that already render the drawer are skipped.
  const SELF_RENDERED = ['product-form', 'iw-cart-recs', 'cart-items', 'cart-drawer'];
  function refreshDrawer() {
    const drawer = document.querySelector('cart-drawer');
    if (!drawer) return;
    fetch(`${(window.routes && window.routes.cart_url) || '/cart'}?section_id=cart-drawer`)
      .then((r) => r.text())
      .then((html) => {
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const fresh = doc.querySelector('.drawer__inner');
        const inner = drawer.querySelector('.drawer__inner');
        if (!fresh || !inner) return;
        inner.innerHTML = fresh.innerHTML;
        const empty = fresh.classList.contains('is-empty');
        inner.classList.toggle('is-empty', empty);
        drawer.classList.toggle('is-empty', empty);
      })
      .catch(() => {});
  }

  // Chat launchers (Shopify Inbox and similar) float over the drawer's buttons on phones.
  function syncChat() {
    const open = !!document.querySelector('cart-drawer.active');
    document.documentElement.classList.toggle('iw-cart-drawer-open', open);
  }
  document.addEventListener('DOMContentLoaded', () => {
    // Highlight the newest line once after an add (product form or "Agregar"), not on quantity changes.
    const CartDrawer = customElements.get('cart-drawer');
    if (CartDrawer && !CartDrawer.prototype.iwPatched) {
      const render = CartDrawer.prototype.renderContents;
      CartDrawer.prototype.renderContents = function (parsedState) {
        render.call(this, parsedState);
        this.classList.add('iw-just-added');
        clearTimeout(this.iwAddedTimer);
        this.iwAddedTimer = setTimeout(() => this.classList.remove('iw-just-added'), 1200);
      };
      const open = CartDrawer.prototype.open;
      CartDrawer.prototype.open = function (triggeredBy) {
        if (onCartPage()) return;
        open.call(this, triggeredBy);
      };
      CartDrawer.prototype.iwPatched = true;
    }

    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
        if (!event || SELF_RENDERED.includes(event.source)) return;
        refreshDrawer();
      });
    }

    const drawer = document.querySelector('cart-drawer');
    if (!drawer) return;
    new MutationObserver(syncChat).observe(drawer, { attributes: true, attributeFilter: ['class'] });
    syncChat();
  });
})();
